import React, { useState } from 'react';
import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import commaNumber from 'comma-number';
import moment from 'moment';
import { Button, Row, Space } from 'antd';

import API from 'helpers/API';
import { getReclaimStatus } from 'utils/CodeMapper';
import { getDisplayName } from 'utils/getDisplayName';
import { downloadExcelFile } from 'utils/FileDownloader';
import { showLoading } from 'store/loadingSlice';

import styles from './index.module.scss';
import { HeadingH1, UnitTable } from 'components/ui';

const cx = classNames.bind(styles);

const sortText = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return String(a).localeCompare(String(b));
};

const sortNumber = (a, b) => Number(a ?? 0) - Number(b ?? 0);

const sortDateTime = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return new Date(a).getTime() - new Date(b).getTime();
};

const Page = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [total, setTotal] = useState(0);
  const [reclaims, setReclaims] = useState();

  const download = () => {
    const records = reclaims.map((reclaim) => {
      return {
        A: getReclaimStatus(reclaim.status),
        B: reclaim.reclaimDate ? moment(reclaim.reclaimDate, 'YYYYMMDD').format('YYYY-MM-DD') : '',
        C: reclaim.quantity,
        D: reclaim.customerCompany ?? '',
        E: reclaim.customerName ?? '',
        F: reclaim.customerAddress ?? '',
        G: moment(reclaim.createdAt).format('YYYY-MM-DD HH:mm:ss'),
        H: getDisplayName(reclaim.createdBy),
        I: moment(reclaim.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
        J: getDisplayName(reclaim.updatedBy),
      };
    });

    downloadExcelFile(
      [
        {
          A: '상태',
          B: '회수일자',
          C: '수량',
          D: '업체명',
          E: '성명',
          F: '주소',
          G: '등록일시',
          H: '등록자',
          I: '수정일시',
          J: '수정자',
        },
      ],
      records,
      `회수내역-${moment().format('YYYYMMDDHHmmss')}`,
    );
  };

  useMountEffect(() => {
    dispatch(showLoading(true));

    // API: 회수 내역 조회
    API.get('/reclaims')
      .then(({ success, data }) => {
        if (success) {
          setTotal(data.totalCount);
          setReclaims(data.reclaims);
        }
      })
      .finally(() => dispatch(showLoading(false)));
  });

  return (
    <div className={cx('container')}>
      <HeadingH1 title="회수 내역" />

      <Row align={'middle'} justify={'space-between'}>
        <Space>
          <div className={cx('total')}>{`총 ${commaNumber(total)}`}건</div>
        </Space>
        <Space>
          <Button onClick={download} type="primary">
            엑셀 다운로드
          </Button>

          <Button type="primary" onClick={() => navigate('/reclaims/new')}>
            신규
          </Button>
        </Space>
      </Row>

      <UnitTable
        onRow={(record) => {
          return {
            onClick: () => navigate(`/reclaims/${record.rid}`),
          };
        }}
        rowKey="rid"
        columns={[
          {
            key: 'status',
            dataIndex: 'status',
            title: '상태',
            sorter: (a, b) => sortText(getReclaimStatus(a.status), getReclaimStatus(b.status)),
            render: (_, record) => getReclaimStatus(record.status),
            width: 84,
            align: 'center',
          },
          {
            key: 'reclaimDate',
            dataIndex: 'reclaimDate',
            title: '회수일자',
            sorter: (a, b) => sortText(a.reclaimDate, b.reclaimDate),
            render: (_, record) => (record.reclaimDate ? moment(record.reclaimDate, 'YYYYMMDD').format('YYYY-MM-DD') : '-'),
            width: 116,
            align: 'center',
          },
          {
            key: 'quantity',
            title: '수량',
            dataIndex: 'quantity',
            sorter: (a, b) => sortNumber(a.quantity, b.quantity),
            render: (_, record) => Number(record.quantity).toLocaleString(),
            width: 72,
            align: 'center',
          },
          {
            key: 'customerCompany',
            dataIndex: 'customerCompany',
            title: '업체명',
            ellipsis: true,
            width: 136,
          },
          {
            key: 'customerName',
            dataIndex: 'customerName',
            title: '성명',
            width: 116,
            render: (_, record) => <div className={cx('customerName')}>{record.customerName ?? '-'}</div>,
          },
          {
            key: 'customerAddress',
            dataIndex: 'customerAddress',
            title: '주소',
            ellipsis: true,
            width: 220,
            render: (_, record) => record.customerAddress ?? '-',
          },
          {
            key: 'createdBy',
            dataIndex: 'createdBy',
            title: '등록자',
            render: (_, record) => <div className={cx('person')}>{getDisplayName(record.createdBy)}</div>,
            width: 92,
            align: 'center',
          },
          {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: '등록일시',
            sorter: (a, b) => sortDateTime(a.createdAt, b.createdAt),
            render: (_, record) => moment(record.createdAt).format('YYYY-MM-DD HH:mm:ss'),
            width: 156,
            align: 'center',
          },
          {
            key: 'updatedBy',
            dataIndex: 'updatedBy',
            title: '수정자',
            render: (_, record) => <div className={cx('person')}>{getDisplayName(record.updatedBy)}</div>,
            width: 92,
            align: 'center',
          },
          {
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            title: '수정일시',
            sorter: (a, b) => sortDateTime(a.updatedAt, b.updatedAt),
            render: (_, record) => moment(record.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
            width: 156,
            align: 'center',
          },
        ]}
        dataSource={reclaims}
      />
    </div>
  );
};

export default Page;
